/**
 * useNotificationNavigation
 *
 * Centralised "click a global notification" handler.
 *
 * Behaviour on every click:
 *  1. Resolve navigation target (action_url first, then structured routing).
 *  2. Extract the entity ID from the clicked notification.
 *  3. Optimistically remove ALL notifications for that entity from local state.
 *  4. Close the panel immediately.
 *  5. Navigate to resolved target.
 *  6. Backend: POST /consume-by-context (bulk delete for entity).
 *  7. If backend fails → restore ALL removed notifications.
 *
 * This ensures clicking notification N for Chat-X also removes notifications
 * N-1, N-2 etc. that exist for the same chat.
 *
 * Security: all backend calls are scoped to the authenticated user + tenant
 * by the backend endpoints — no extra work needed on the frontend.
 */

import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import logger from '../utils/logger';

// ---------------------------------------------------------------------------
// Routing matrix
// ---------------------------------------------------------------------------

/**
 * Build the navigation URL from structured notification fields.
 * Returns null if no safe target can be resolved.
 *
 * Priority:
 *  1. action_url that belongs to the current origin (starts with /)
 *  2. Structured routing by event_type / object_type
 *  3. null  → caller should NOT navigate
 *
 * @param {object} notification
 * @returns {string|null}
 */
export function resolveNotificationTarget(notification) {
  if (!notification) return null;

  const {
    action_url,
    link,
    event_type,
    object_type,
    object_id,
    context_id,
    metadata = {},
    title = '',
    message = '',
  } = notification;

  const titleLower = String(title || '').toLowerCase();
  const messageLower = String(message || '').toLowerCase();
  const eventLower = String(event_type || '').toLowerCase();
  const objectLower = String(object_type || '').toLowerCase();
  const rawUrl = action_url || link || '';

  // 1. Explicit Check for Public Lead Capture Link approval notifications
  // Handles both newly generated notifications and historical DB notifications
  // that have legacy action_url="/commerce/revenue/leads"
  const isPublicLink =
    objectLower === 'public_link_request' ||
    (metadata && metadata.category === 'public_links') ||
    (metadata && metadata.request_id && String(metadata.request_id).toUpperCase().startsWith('REQ-LINK')) ||
    titleLower.includes('public lead capture') ||
    titleLower.includes('public capture link') ||
    titleLower.includes('public link') ||
    messageLower.includes('public capture link') ||
    messageLower.includes('public lead capture');

  if (isPublicLink) {
    const reqId =
      (metadata && (metadata.request_id || metadata.object_id)) ||
      object_id ||
      context_id ||
      null;
    return reqId
      ? `/workspace/approvals?category=public_links&request_id=${encodeURIComponent(reqId)}`
      : '/workspace/approvals?category=public_links';
  }

  // 2. Intercept any other approval notifications that might have obsolete /commerce/revenue/leads action_urls
  if (
    rawUrl &&
    (rawUrl.includes('/commerce/revenue/leads') || rawUrl.includes('/commerce/revenue-workflow/leads')) &&
    (eventLower.includes('approval') || titleLower.includes('approval'))
  ) {
    return '/workspace/approvals';
  }

  // 3. Prefer a valid first-party action_url (if not the obsolete leads URL above)
  if (rawUrl && (rawUrl.startsWith('/') || rawUrl.startsWith(window.location.origin))) {
    const path = rawUrl.startsWith(window.location.origin)
      ? rawUrl.slice(window.location.origin.length)
      : rawUrl;
    if (path.includes('/commerce/revenue/leads') && (eventLower.includes('approval') || titleLower.includes('approval'))) {
      return '/workspace/approvals';
    }
    return path;
  }

  const t = (event_type || object_type || '').toLowerCase();

  // 4. Chat messages
  if (t.includes('chat')) {
    const chatId =
      context_id ||
      object_id ||
      (metadata && metadata.chat_id) ||
      (metadata && metadata.context_id) ||
      null;
    return chatId ? `/workspace/chats?chat=${encodeURIComponent(chatId)}` : '/workspace/chats';
  }

  // 5. Channel messages / mentions / invites
  if (t.includes('channel')) {
    const channelId =
      object_id ||
      context_id ||
      (metadata && metadata.channel_id) ||
      null;
    return channelId
      ? `/workspace/channels?channel=${encodeURIComponent(channelId)}`
      : '/workspace/channels';
  }

  // 6. Tasks
  if (t.includes('task')) {
    const taskId = object_id || context_id || (metadata && metadata.task_id) || null;
    return taskId
      ? `/workspace/tasks/${encodeURIComponent(taskId)}`
      : '/workspace/tasks';
  }

  // 7. General Approvals
  if (t.includes('approval')) {
    return '/workspace/approvals';
  }

  // 8. Calendar events
  if (t.includes('calendar')) {
    const eventId = object_id || context_id || (metadata && metadata.event_id) || null;
    return eventId
      ? `/workspace/calendar?event_id=${encodeURIComponent(eventId)}`
      : '/workspace/calendar';
  }

  // 9. Connect / meetings
  if (t.includes('meeting') || t.includes('action_item') || t.includes('connect')) {
    return '/workspace/connect';
  }

  // 10. Pages / vault / deck / grid
  if (t.includes('page')) return '/workspace/pages';
  if (t.includes('vault')) return '/workspace/vault';
  if (t.includes('deck')) return '/workspace/deck';
  if (t.includes('grid')) return '/workspace/grid';

  // 11. No resolvable target
  return null;
}

/**
 * Extract the entity/context ID from a notification — the ID that represents
 * the entity this notification belongs to (chat, channel, task…).
 *
 * Checks all possible fields that different producers may have used.
 *
 * @param {object} notification
 * @returns {string|null}
 */
export function resolveEntityId(notification) {
  if (!notification) return null;
  const { context_id, object_id, metadata = {} } = notification;
  return (
    context_id ||
    object_id ||
    (metadata && metadata.chat_id) ||
    (metadata && metadata.context_id) ||
    (metadata && metadata.request_id) ||
    null
  );
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

/**
 * @param {object} opts
 * @param {function} opts.onConsumeByContext   Called with ({contextId, objectType}) to bulk-remove all entity notifications.
 * @param {function} opts.onClose              Called to close the notification panel.
 */
export function useNotificationNavigation({ onConsumeByContext, onClose }) {
  const navigate = useNavigate();

  const handleClick = useCallback(async (notification) => {
    if (!notification) return;

    const target = resolveNotificationTarget(notification);
    const entityId = resolveEntityId(notification);
    const objectType = notification.object_type || null;

    // 1. Close the panel immediately for a snappy feel
    if (typeof onClose === 'function') onClose();

    // 2. Consume ALL notifications for this entity (optimistic + backend)
    //    This handles the case where there are multiple notifications for the
    //    same chat/channel/task — all are dismissed at once.
    if (entityId && typeof onConsumeByContext === 'function') {
      await onConsumeByContext({ contextId: entityId, objectType });
    }

    // 3. Navigate to resolved target
    if (target) {
      navigate(target);
    }
  }, [navigate, onConsumeByContext, onClose]);

  return { handleClick, resolveNotificationTarget, resolveEntityId };
}

export default useNotificationNavigation;
